import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { ColumnConfig } from '../../shared/components/table-header/header-config.model';

export interface VisibilityBreakpoint {
  minWidth: number;
  visiblePriority: number;
}

@Injectable()
export class WidgetVisibilityService implements OnDestroy {
  private resizeObserver: ResizeObserver | undefined;
  private currentWidth = 0;

  // Default breakpoints - can be overridden
  private defaultBreakpoints: VisibilityBreakpoint[] = [
    { minWidth: 0, visiblePriority: 1 }, // mobile
    { minWidth: 576, visiblePriority: 2 }, // small
    { minWidth: 768, visiblePriority: 3 }, // medium
    { minWidth: 992, visiblePriority: 4 }, // large
  ];

  private breakpoints = [...this.defaultBreakpoints];

  public width$ = new BehaviorSubject<number>(0);

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  setupResizeObserver(element: HTMLElement): void {
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          this.currentWidth = entry.contentRect.width;
          this.width$.next(this.currentWidth);
        }
      });
      this.resizeObserver.observe(element);
    }
  }

  setBreakpoints(breakpoints: VisibilityBreakpoint[]): void {
    this.breakpoints = breakpoints.sort((a, b) => a.minWidth - b.minWidth);
  }

  isColumnVisible(priority: number, currentWidth?: number): boolean {
    const width: number = currentWidth ?? this.currentWidth;
    const applicableBreakpoint: VisibilityBreakpoint | undefined = this.breakpoints
      .filter(breakpoint => width >= breakpoint.minWidth)
      .pop();
    const visibleColumns: number = applicableBreakpoint?.visiblePriority ?? 1;

    return priority <= visibleColumns;
  }

  getVisibleColumns<T extends ColumnConfig>(columns: T[], currentWidth?: number): T[] {
    const width: number = currentWidth ?? this.currentWidth;

    return columns.filter((column) => {
      if (!column.priority) {
        return true;
      }
      return this.isColumnVisible(column.priority, width);
    });
  }

  disconnect(): void {
    this.resizeObserver?.disconnect();
  }
}
